import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { FAQBlock, FunnelBlock, WhyClientsBlock } from '@/components/Blocks';
import { SERVICES } from '@/data/services';
import { GENERAL_FAQ } from '@/data/faqs';
import { SERVICES as SERVICES_TID } from '@/constants/testIds';
import { buildFAQSchema } from '@/data/schema';
import useSEO from '@/hooks/useSEO';

export default function Services() {
  useSEO({
    title: 'Services — Operations, Websites, SEO, Social Media',
    description:
      'Reliable Virtual Support, Website Design & Development, SEO Optimization, and Social Media Management for service-based businesses. Engagements scoped to your business.',
    path: '/services',
    schema: buildFAQSchema(GENERAL_FAQ),
  });

  return (
    <div data-testid={SERVICES_TID.page}>
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl">
            <Eyebrow>Services</Eyebrow>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight mt-6">
              Four ways to work<br/><span className="font-serif-italic italic text-[#C9A24D]">together.</span>
            </h1>
            <p className="mt-8 text-lg text-[#1C1C1C]/80 leading-relaxed">
              Each engagement is shaped around your business, your team and your stage of growth. We will land on the right scope together during the discovery call — never before.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-[#E6CFC7]/70">
        {SERVICES.map((s, idx) => (
          <Link to={`/services/${s.slug}`} key={s.slug} data-testid={`${SERVICES_TID.card}-${s.slug}`} className="block border-b border-[#E6CFC7]/70 hover:bg-[#FBF7F2] transition-colors group">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-1 num-marker">{String(idx + 1).padStart(2, '0')}</div>
              <div className="lg:col-span-5">
                <h2 className="font-serif-display text-3xl lg:text-5xl leading-[1.05] tracking-tight">{s.name}</h2>
              </div>
              <div className="lg:col-span-5 text-[#1C1C1C]/75 leading-relaxed">{s.short}</div>
              <div className="lg:col-span-1 flex lg:justify-end">
                <ArrowUpRight size={28} className="text-[#1C1C1C]/40 group-hover:text-[#C9A24D] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500" />
              </div>
            </div>
          </Link>
        ))}
      </section>

      <WhyClientsBlock />

      <FAQBlock
        eyebrow="General FAQ"
        title="Common questions,"
        italic="answered plainly."
        items={GENERAL_FAQ}
        testidPrefix="services-general-faq"
      />

      <FunnelBlock />

      <section className="py-24 lg:py-32 text-center">
        <div className="max-w-[900px] mx-auto px-6">
          <SectionHeader align="center" eyebrow="Begin" title="Not sure which fits?" italic="That is exactly what the discovery call is for." />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="services-cta-book-call">Book a Discovery Call</Link>
            <Link to="/contact" className="btn-ghost" data-testid="services-cta-consult">Request a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
